// 2026-05-26 - FormuLearning - Step 2 QTPP 편집 테이블
// 2026-09-01 수정 - 이슈 #58 S10(#70, M84-49): **접기**를 붙인다. 렌더 위치는 Step 3 맨
//   앞이고(260602 회의 결정), 표 + 경고 2종이 세로를 크게 차지해 정작 작업 대상인
//   Target Design(변수·초기 설계)이 화면 밖으로 밀린다. QTPP 는 Step 2 에서 채워 Step 3
//   에서는 참조하는 자료이므로 **기본 접힘**이다.
// 2026-11-03 수정 - 이슈 #114: viewer 는 QTPP 편집·저장 잠금(`locked` 에 합침, 펼침은 허용).
// 파일 위치: frontend/src/features/step2/QtppSection.tsx

import { useState } from 'react'
import type { QTPPRow } from '../../api/step2'
import { useCanWrite, READ_ONLY_TITLE } from '../../hooks/useCanWrite'

interface Props {
  rows: QTPPRow[]
  onSave: (rows: QTPPRow[]) => Promise<void>
  /** 초기 접힘 여부(M84-49). 기본은 접힘 — Step 3의 작업 대상은 Target Design이다. */
  defaultCollapsed?: boolean
  /**
   * 읽기 전용(M84-48). 편집(행 추가·셀 입력·저장)만 잠그고 **개폐 토글은 잠그지 않는다** —
   * 접기는 편집이 아니라 열람 조작이고, 토글까지 죽으면 잠긴 스텝에서 QTPP를 **펼쳐
   * 볼 수조차 없어** 「값은 확인만 가능」이라는 잠금의 전제가 깨진다(260901 채증이 잡음).
   */
  locked?: boolean
}

export default function QtppSection({ rows, onSave, defaultCollapsed = true, locked: lockedProp = false }: Props) {
  // viewer(#114) — 기존 잠금(설계 확정)에 합친다. 펼침 토글은 잠금 밖이라 그대로 열린다.
  const canWrite = useCanWrite()
  const locked = lockedProp || !canWrite
  const [localRows, setLocalRows] = useState<QTPPRow[]>(rows)
  const [saving, setSaving] = useState(false)
  // 접힘 상태는 세션 내 유지된다(CardShell 과 같은 규약 — 프로젝트 전환 시 상위 리마운트로 재계산).
  const [collapsed, setCollapsed] = useState(defaultCollapsed)

  const updateCell = (idx: number, field: keyof QTPPRow, value: string) => {
    const updated = [...localRows]
    updated[idx] = { ...updated[idx], [field]: value }
    setLocalRows(updated)
  }

  const addRow = () => {
    setLocalRows([...localRows, { attribute: '', detail: '', target: '', rationale: '' }])
  }

  const removeRow = (idx: number) => {
    setLocalRows(localRows.filter((_, i) => i !== idx))
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      await onSave(localRows)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="mb-4" data-testid="qtpp-section">
      <div className="mb-2 flex items-center justify-between">
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="flex items-center gap-2 text-left"
          data-testid="qtpp-toggle"
          aria-expanded={!collapsed}
        >
          <h4 className="text-xs font-semibold uppercase tracking-wider text-text-secondary">
            Quality Target Product Profile (QTPP)
          </h4>
          <span className="text-xs text-text-secondary">{collapsed ? '▼' : '▲'}</span>
          {collapsed && (
            <span className="text-[10px] text-text-muted">{localRows.length}행</span>
          )}
        </button>
        {/* 편집 버튼은 잠금 대상이다 — 토글(왼쪽)과 달리 값을 바꾼다. */}
        <fieldset disabled={locked} title={canWrite ? undefined : READ_ONLY_TITLE} className={`m-0 flex min-w-0 gap-2 border-0 p-0 ${collapsed ? 'hidden' : ''}`}>
          <button
            onClick={addRow}
            className="rounded px-2 py-1 text-[10px] text-text-secondary transition-colors hover:bg-bg-input"
            style={{ border: '1px solid var(--border-soft)' }}
          >
            + Add Row
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="rounded bg-accent-teal px-3 py-1 text-[10px] font-semibold text-[#1a1b1e] transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save QTPP'}
          </button>
        </fieldset>
      </div>
      {!collapsed && (
      <fieldset disabled={locked} title={canWrite ? undefined : READ_ONLY_TITLE} className="m-0 min-w-0 border-0 p-0">
      <p
        className="mb-2 rounded border px-3 py-2 text-[10px] leading-relaxed"
        style={{
          borderColor: 'color-mix(in srgb, var(--color-warn) 35%, transparent)',
          background: 'color-mix(in srgb, var(--color-warn) 10%, transparent)',
          color: 'var(--color-warn)',
        }}
      >
        ⚠️ 제공된 초기값은 보편적 가이드라인에 따른 참고 수치이며, 개발 약물 및 공정서 기준에 맞춰 수정이 필요합니다.
      </p>
      <div
        className="mb-2 rounded border px-3 py-2 text-[10px] leading-relaxed"
        style={{
          borderColor: 'color-mix(in srgb, var(--color-warn) 35%, transparent)',
          background: 'color-mix(in srgb, var(--color-warn) 10%, transparent)',
          color: 'var(--color-warn)',
        }}
      >
        <span className="font-semibold">🚧 개발 노트</span> 사용자 참고용 QTPP 초안 전달 예정 (6/25~)
      </div>
      <div className="overflow-x-auto rounded border" style={{ borderColor: 'var(--border-subtle)' }}>
        <table className="w-full text-[11px]">
          <thead>
            <tr style={{ backgroundColor: 'var(--color-bg-panel)' }}>
              <th className="w-8 px-2 py-1.5 text-text-secondary">#</th>
              <th className="px-2 py-1.5 text-left font-medium text-text-secondary">Attribute</th>
              <th className="px-2 py-1.5 text-left font-medium text-text-secondary">Detail</th>
              <th className="px-2 py-1.5 text-left font-medium text-text-secondary">Target</th>
              <th className="px-2 py-1.5 text-left font-medium text-text-secondary">Rationale</th>
              <th className="w-8 px-2 py-1.5" />
            </tr>
          </thead>
          <tbody>
            {localRows.map((row, i) => (
              <tr key={i} className="border-t" style={{ borderColor: 'var(--border-subtle)' }}>
                <td className="px-2 py-1 text-center text-text-secondary">{i + 1}</td>
                {(['attribute', 'detail', 'target', 'rationale'] as const).map((field) => (
                  <td key={field} className="px-1 py-1">
                    <input
                      type="text"
                      value={row[field]}
                      onChange={(e) => updateCell(i, field, e.target.value)}
                      className="w-full rounded bg-bg-input px-2 py-1 text-[11px] text-text-body outline-none focus:ring-1"
                      style={{ boxShadow: 'none' }}
                    />
                  </td>
                ))}
                <td className="px-2 py-1 text-center">
                  <button onClick={() => removeRow(i)} className="text-text-secondary hover:text-danger text-[10px]">✕</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </fieldset>
      )}
    </div>
  )
}
